import { AdminLayout, useTranslation } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { RouteProps } from "@opendash/router";
import { Button, DatePicker, Divider, Flex, Typography, Upload } from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useRef } from "react";
import { RouteTypeSelect } from "../../components/RouteTypeSelect";
import { GTFSConverter } from "../../js/kommuter/GTFS/GTFSConverter";
import { KommuterImportPageState } from "../KommuterImportPageState";

export default observer(({ state }: RouteProps<KommuterImportPageState>) => {
  const t = useTranslation();
  const ref = useRef(null);
  const [isKommuterInstance, setIsKommuterInstance] = React.useState(false);

  async function convertKommuterToGTFS() {
    try {
      const converter = new GTFSConverter();
      await converter.convertToGTFS();
      const data = converter.getJSONData();
      await state.useKommuterData(data);
    } catch (e) {
      console.error(e);
    }
  }

  // check if GTFS Plugin is in Kommuter Instance
  useEffect(() => {
    async function fetchKommuterInstance() {
      try {
        const kommuterInstance = await new Parse.Query(
          "RideSharingOffer"
        ).first();
        setIsKommuterInstance(kommuterInstance !== undefined);
      } catch (e) {
        setIsKommuterInstance(false);
      }
    }
    fetchKommuterInstance();
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.scrollTop = ref.current.scrollHeight;
  }, [state.messages]);

  return (
    <AdminLayout>
      <h1>{t("gtfs:import.kommuter.page.title")}</h1>
      <p>{t("gtfs:import.kommuter.page.description")}</p>

      {isKommuterInstance && (
        <Button onClick={convertKommuterToGTFS}>
          {t("gtfs:import.kommuter.import_current")}
        </Button>
      )}

      <Upload {...state.getUploadProps()}>
        <Button
          icon={<Icon icon="fa:upload" />}
          disabled={state.step !== "upload"}
        >
          {t("gtfs:import.button.upload")}
        </Button>
      </Upload>

      <Divider />

      <Flex vertical style={{ maxHeight: 400, overflow: "auto" }} ref={ref}>
        {state.messages.map((message, index) => (
          <Typography.Text key={index} type={message.status}>
            {t(message.text, message.translationData)}
          </Typography.Text>
        ))}
      </Flex>

      <Divider />

      {state.step === "convert_routes_form" && (
        <Flex vertical>
          <RouteTypeSelect onChange={(type) => state.set("routeType", type)} />
          <Button
            onClick={() => state.convertRoutes()}
            disabled={state?.routeType === -1}
          >
            {t("gtfs:import.kommuter.confirm")}
          </Button>
        </Flex>
      )}

      {state.step === "convert_trips_form" && (
        <Flex vertical>
          <DatePicker.RangePicker
            picker="date"
            onChange={(dates) => {
              if (!dates) return;
              state.set("startDate", dates[0]?.toDate());
              state.set("endDate", dates[1]?.toDate());
            }}
          />
          <Button
            onClick={() => state.convertTrips()}
            disabled={!state?.startDate || !state?.endDate}
          >
            {t("gtfs:import.kommuter.confirm")}
          </Button>
        </Flex>
      )}
    </AdminLayout>
  );
});
